import axios from 'axios';

import { API_BASE_URL as API_URL } from '@/config/apiBaseUrl';

// Create axios instance with proper config
const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to all requests
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const API_BASE = '/manufacturing';

// ========================================
// Type Definitions
// ========================================

export enum OperationType {
  MACHINING = 'Machining',
  ASSEMBLY = 'Assembly',
  WELDING = 'Welding',
  PAINTING = 'Painting',
  QUALITY_CHECK = 'Quality Check',
  PACKING = 'Packing',
  MATERIAL_HANDLING = 'Material Handling',
  HEAT_TREATMENT = 'Heat Treatment',
  SURFACE_TREATMENT = 'Surface Treatment',
  CUSTOM = 'Custom'
}

export enum SkillLevel {
  BASIC = 'Basic',
  INTERMEDIATE = 'Intermediate',
  ADVANCED = 'Advanced',
  EXPERT = 'Expert'
}

export enum QualityCheckpointType {
  FIRST_PIECE_INSPECTION = 'First Piece Inspection',
  IN_PROCESS = 'In-Process',
  FINAL_INSPECTION = 'Final Inspection',
  STATISTICAL = 'Statistical',
  VISUAL = 'Visual'
}

export enum DependencyType {
  SEQUENTIAL = 'Sequential',
  PARALLEL = 'Parallel',
  CONDITIONAL = 'Conditional',
  START_TO_START = 'Start-to-Start',
  FINISH_TO_START = 'Finish-to-Start',
  FINISH_TO_FINISH = 'Finish-to-Finish'
}

export enum OperationStatus {
  PENDING = 'Pending',
  IN_PROGRESS = 'In Progress',
  COMPLETED = 'Completed',
  PAUSED = 'Paused',
  CANCELLED = 'Cancelled'
}

export interface Operation {
  id: string;
  company_id: string;
  operation_code: string;
  operation_name: string;
  operation_type: OperationType;
  default_workstation_type?: string;
  standard_setup_time_hours: number;
  standard_run_time_per_unit_hours: number;
  standard_teardown_time_hours: number;
  per_hour_cost: number;
  skill_level_required: SkillLevel;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface BOMOperation {
  id: string;
  company_id: string;
  bom_id: string;
  operation_id: string;
  sequence_number: number;
  workstation_id?: string;
  setup_time_hours: number;
  run_time_per_unit_hours: number;
  teardown_time_hours: number;
  batch_size: number;
  requires_quality_check: boolean;
  quality_checkpoint_type?: QualityCheckpointType;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  operation?: Operation;
  workstation?: any;
}

export interface OperationDependency {
  id: string;
  company_id: string;
  bom_id: string;
  operation_id: string;
  depends_on_operation_id: string;
  dependency_type: DependencyType;
  lead_time_hours: number;
  lag_time_hours: number;
  condition_description?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  operation?: BOMOperation;
  depends_on_operation?: BOMOperation;
}

export interface OperationTimeLog {
  id: string;
  company_id: string;
  work_order_id: string;
  /** Set by API when listing/fetching logs for display */
  work_order_number?: string | null;
  bom_operation_id: string;
  operation_id: string;
  workstation_id: string;
  operator_id?: string;
  scheduled_start_time?: string;
  actual_start_time?: string;
  scheduled_end_time?: string;
  actual_end_time?: string;
  actual_setup_time_hours?: number;
  actual_run_time_hours?: number;
  actual_teardown_time_hours?: number;
  quantity_completed: number;
  quantity_rejected: number;
  quantity_rework: number;
  efficiency_percentage?: number;
  status: OperationStatus;
  notes?: string;
  created_at: string;
  updated_at: string;
  bom_operation?: BOMOperation;
  operation?: Operation;
  workstation?: any;
}

// ========================================
// Operations Master API
// ========================================

export const operationsAPI = {
  create: (data: Partial<Operation>) => apiClient.post(`${API_BASE}/operations`, data),
  getAll: (company_id: string, params?: { search?: string; active_only?: boolean }) =>
    apiClient.get(`${API_BASE}/operations`, { params: { company_id, ...params } }),
  getById: (id: string) => apiClient.get(`${API_BASE}/operations/${id}`),
  getByType: (company_id: string, type: OperationType) =>
    apiClient.get(`${API_BASE}/operations/type/${type}`, { params: { company_id } }),
  update: (id: string, data: Partial<Operation>) => apiClient.put(`${API_BASE}/operations/${id}`, data),
  delete: (id: string) => apiClient.delete(`${API_BASE}/operations/${id}`),
  activate: (id: string) => apiClient.post(`${API_BASE}/operations/${id}/activate`),
  deactivate: (id: string) => apiClient.post(`${API_BASE}/operations/${id}/deactivate`),
};

// ========================================
// BOM Operations (Routing) API
// ========================================

export const bomOperationsAPI = {
  create: (data: Partial<BOMOperation>) => apiClient.post(`${API_BASE}/bom-operations`, data),
  getByBOM: (bomId: string) => apiClient.get(`${API_BASE}/bom-operations/bom/${bomId}`),
  getById: (id: string) => apiClient.get(`${API_BASE}/bom-operations/${id}`),
  update: (id: string, data: Partial<BOMOperation>) => apiClient.put(`${API_BASE}/bom-operations/${id}`, data),
  delete: (id: string) => apiClient.delete(`${API_BASE}/bom-operations/${id}`),
  resequence: (bomId: string, operations: Array<{ id: string; sequence_number: number }>) =>
    apiClient.post(`${API_BASE}/bom-operations/bom/${bomId}/resequence`, { operations }),
  calculateTotalTime: (bomId: string, quantity: number) =>
    apiClient.get(`${API_BASE}/bom-operations/bom/${bomId}/total-time`, { params: { quantity } }),
  getQualityCheckpoints: (bomId: string) =>
    apiClient.get(`${API_BASE}/bom-operations/bom/${bomId}/quality-checkpoints`),
};

// ========================================
// Operation Dependencies API
// ========================================

export const operationDependenciesAPI = {
  create: (data: Partial<OperationDependency>) => apiClient.post(`${API_BASE}/operation-dependencies`, data),
  getByBOM: (bomId: string) => apiClient.get(`${API_BASE}/operation-dependencies/bom/${bomId}`),
  getByOperation: (operationId: string) =>
    apiClient.get(`${API_BASE}/operation-dependencies/operation/${operationId}`),
  getDependents: (operationId: string) =>
    apiClient.get(`${API_BASE}/operation-dependencies/operation/${operationId}/dependents`),
  getById: (id: string) => apiClient.get(`${API_BASE}/operation-dependencies/${id}`),
  update: (id: string, data: Partial<OperationDependency>) =>
    apiClient.put(`${API_BASE}/operation-dependencies/${id}`, data),
  delete: (id: string) => apiClient.delete(`${API_BASE}/operation-dependencies/${id}`),
  getCriticalPath: (bomId: string) => apiClient.get(`${API_BASE}/operation-dependencies/bom/${bomId}/critical-path`),
};

// ========================================
// Operation Time Logs API
// ========================================

export const operationTimeLogsAPI = {
  create: (data: Partial<OperationTimeLog>) => apiClient.post(`${API_BASE}/operation-time-logs`, data),
  getByWorkOrder: (workOrderId: string) =>
    apiClient.get(`${API_BASE}/operation-time-logs/work-order/${workOrderId}`),
  getByStatus: (company_id: string, status: OperationStatus) =>
    apiClient.get(`${API_BASE}/operation-time-logs/status/${status}`, { params: { company_id } }),
  getByOperator: (operatorId: string, start_date: string, end_date: string) =>
    apiClient.get(`${API_BASE}/operation-time-logs/operator/${operatorId}`, { params: { start_date, end_date } }),
  getById: (id: string) => apiClient.get(`${API_BASE}/operation-time-logs/${id}`),
  update: (id: string, data: Partial<OperationTimeLog>) =>
    apiClient.put(`${API_BASE}/operation-time-logs/${id}`, data),
  startOperation: (id: string) => apiClient.post(`${API_BASE}/operation-time-logs/${id}/start`),
  completeOperation: (id: string, data: any) => apiClient.post(`${API_BASE}/operation-time-logs/${id}/complete`, data),
  pauseOperation: (id: string) => apiClient.post(`${API_BASE}/operation-time-logs/${id}/pause`),
  resumeOperation: (id: string) => apiClient.post(`${API_BASE}/operation-time-logs/${id}/resume`),
  getOperatorEfficiency: (operatorId: string, start_date: string, end_date: string) =>
    apiClient.get(`${API_BASE}/operation-time-logs/operator/${operatorId}/efficiency`, {
      params: { start_date, end_date },
    }),
};

export default {
  operations: operationsAPI,
  bomOperations: bomOperationsAPI,
  operationDependencies: operationDependenciesAPI,
  operationTimeLogs: operationTimeLogsAPI,
};

